// @flow
import DecimalFormat from "../../../utils/number/DecimalFormat";
import {
  parseNumberToString,
  parseToNumber,
} from "../../../utils/number/parseNumbers";

import type { IConstraintModel } from "../../types";

const INVALID_INTEGER_ID = "Constraint.Number.InvalidInteger";

/**
 */
class NumberFormatConstraint implements IConstraintModel {
  _decimalSeparator: string = ",";
  _groupingSeparator: string = ".";
  _format: string = "0";

  /**
   */
  constructor(
    groupingSeparator: string,
    decimalSeparator: string,
    format: string | null,
  ) {
    this._decimalSeparator = decimalSeparator;
    this._groupingSeparator = groupingSeparator;
    this._format = format ?? "0";
  }

  /**
   */
  get id(): string {
    if (this.maxDigits === 0) {
      return INVALID_INTEGER_ID;
    }

    return "Constraint.Number.InvalidDecimal";
  }

  /**
   */
  get decimalSeparator(): string {
    return this._decimalSeparator;
  }

  /**
   */
  get groupingSeparator(): string {
    return this._groupingSeparator;
  }

  /**
   */
  get format(): string {
    return this._format;
  }

  /**
   */
  get maxDigits(): number {
    return this.format ? new DecimalFormat(this.format).maxFrac : 0;
  }

  /**
   */
  hasValidation(): boolean {
    return true;
  }

  /**
   */
  get defaultMessage(): string {
    if (this.id === INVALID_INTEGER_ID) {
      return "Must be a whole number";
    }

    return "The value is not correct numeric format";
  }

  /**
   */
  get parameters(): { "max-digits": number } {
    return {
      "max-digits": this.maxDigits,
    };
  }

  /**
   */
  isInteger(value: number): boolean {
    return Number.isInteger(value);
  }

  /**
   */
  getNumberOfDigits(value: number): number {
    if (Math.floor(value) === value) {
      return 0;
    }

    const stringValue = parseNumberToString(value);
    return stringValue.split(".")[1].length;
  }

  /**
   */
  isValidDecimal(value: number): boolean {
    if (isNaN(value)) {
      return false;
    }

    const numberOfDigits = this.getNumberOfDigits(value);

    return (
      !isNaN(parseFloat(value)) &&
      isFinite(value) &&
      numberOfDigits <= this.maxDigits
    );
  }

  /**
   */
  validate(value: string | number): boolean {
    if (
      this.maxDigits === 0 &&
      this.decimalSeparator !== "" &&
      value.toString().includes(this.decimalSeparator)
    ) {
      return false;
    }

    const parsedNumber = parseToNumber(
      value,
      this.groupingSeparator,
      this.decimalSeparator,
    );

    if (this.id === INVALID_INTEGER_ID) {
      return this.isInteger(parsedNumber);
    }

    return this.isValidDecimal(parsedNumber);
  }

  /**
   */
  get isMandatoryConstraint(): boolean {
    return false;
  }
}

export default NumberFormatConstraint;
